/**
 * Anonymous device id — a UUID generated on first use and stored in
 * localStorage under `checkthelineup:device:v1`. Sent only when the user
 * voluntarily mints a share, to shard share-tokens across GCS prefixes.
 *
 * Privacy note: this id is not identity. It's an opaque random string
 * the server hashes into a 2-char path prefix and then throws away —
 * we never persist the raw id anywhere.
 */
const KEY = "checkthelineup:device:v1";

function generateUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as { randomUUID(): string }).randomUUID();
  }
  // RFC-ish fallback. Bits aren't as strong but still unique enough for
  // bucket sharding.
  const rnd = (n: number) => Math.floor(Math.random() * n);
  const hex = (n: number) => n.toString(16).padStart(2, "0");
  const part = (bytes: number) => {
    let out = "";
    for (let i = 0; i < bytes; i++) out += hex(rnd(256));
    return out;
  };
  return `${part(4)}-${part(2)}-${part(2)}-${part(2)}-${part(6)}`;
}

/** Returns the device id, generating + persisting one on first call. */
export function getDeviceId(): string {
  if (typeof localStorage === "undefined") return generateUuid();
  try {
    const existing = localStorage.getItem(KEY);
    if (existing && existing.length > 0) return existing;
    const fresh = generateUuid();
    localStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    return generateUuid();
  }
}
